import { useMsal } from "@azure/msal-react";
import { faSignOut } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { CompilationErrors, pgbBuild } from "../../../../common/ProjectGraph";
import { env } from "../../../../env/env";
import {
	selectFetchBackendStatus,
	selectFetchLogicsStatus,
} from "../../../logics/logicsSlice";
import { MenuType } from "../../../menuManager/menu/menuImpl";
import {
	closeMenu,
	openNormalMenu,
	selectCurrentMenu,
} from "../../../menuManager/menuManagerSlice";
import { createWindow } from "../../../windowManager/windowManagerSlice";
import {
	selectEditor,
	selectProjectName,
	updateProjectFileNameOneDrive,
	updateProjectName,
} from "../../editorSlice";
import { Toast } from "../toast/Toast";

import brazilFlag from "/assets/flags/br.svg";
import spanishFlag from "/assets/flags/es.svg";
import usaFlag from "/assets/flags/us.svg";

import css from "./Header.module.css";

interface MenuBarItemProps {
	label: string;
	menuType: MenuType;
}

function MenuBarItem(props: MenuBarItemProps) {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const currentMenu = useAppSelector(selectCurrentMenu);

	const menuBarItems: MenuBarItemProps[] = [
		{ label: t("Project"), menuType: "project" },
		// { label: "Edit", menuType: "edit" },
		{ label: t("View"), menuType: "view" },
		// { label: "Help", menuType: "help" },
	];

	function open(rect: DOMRect) {
		const { left, bottom } = rect;
		dispatch(
			openNormalMenu({
				sourceId: props.menuType,
				position: { x: left, y: bottom },
			}),
		);
	}

	return (
		<div
			className={classNames(css.menuItem, {
				[css.menuItemOpen]: currentMenu?.sourceId === props.menuType,
			})}
			onMouseDown={(event) => event.stopPropagation()}
			onClick={(event) => {
				if (currentMenu === null) {
					open(event.currentTarget.getBoundingClientRect());
				} else if (currentMenu.sourceId === props.menuType) {
					dispatch(closeMenu());
				}
			}}
			onMouseEnter={(event) => {
				if (currentMenu === null) {
					return;
				}

				const menuTypes = menuBarItems.map((item) => item.menuType);
				if (menuTypes.includes(currentMenu.sourceId)) {
					open(event.currentTarget.getBoundingClientRect());
				}
			}}
		>
			{props.label}
		</div>
	);
}

export function ProjectName() {
	const { t } = useTranslation();
	const { instance } = useMsal();
	const dispatch = useAppDispatch();
	const projectName = useAppSelector(selectProjectName);
	const editor = useAppSelector(selectEditor);

	const [editing, setEditing] = useState(false);

	return (
		<div className={css.titleBar}>
			{editing ? (
				<input
					className={css.titleContent}
					defaultValue={projectName}
					autoFocus={true}
					onFocus={(event) => {
						event.target.select();
						event.target.style.width = "0px";
						const boundingRect = event.target.getBoundingClientRect();
						const size = Math.min(
							event.target.scrollWidth + 2 - boundingRect.width,
							300,
						);
						event.target.style.width = `${size}px`;
					}}
					onBlur={(event) => {
						setEditing(false);
						dispatch(updateProjectName(event.currentTarget.value));
						if (editor.oneDrive?.driveId && editor.oneDrive.itemId) {
							dispatch(
								updateProjectFileNameOneDrive({
									name: event.currentTarget.value,
									instance,
									driveId: editor.oneDrive.driveId,
									fileId: editor.oneDrive.itemId,
									t,
								}),
							);
						}
					}}
					onChange={(event) => {
						event.target.style.width = "0px";
						const boundingRect = event.target.getBoundingClientRect();
						const size = Math.min(
							event.target.scrollWidth + 2 - boundingRect.width,
							300,
						);
						event.target.style.width = `${size}px`;
					}}
					onKeyDown={(event) => {
						const key = event.key;

						if (key === "Enter" || key === "Escape") {
							setEditing(false);
						}

						if (key === "Enter") {
							dispatch(updateProjectName(event.currentTarget.value));
							if (editor.oneDrive?.driveId && editor.oneDrive.itemId) {
								dispatch(
									updateProjectFileNameOneDrive({
										name: event.currentTarget.value,
										instance,
										driveId: editor.oneDrive.driveId,
										fileId: editor.oneDrive.itemId,
										t,
									}),
								);
							}
						}
					}}
				/>
			) : (
				<div className={css.titleContent} onClick={() => setEditing(true)}>
					{projectName}
				</div>
			)}
		</div>
	);
}

function VersionBox(props: {
	type: string;
	version: string;
	tooltip?: React.ReactNode;
	mirrored?: boolean;
}) {
	const [tooltipVisible, setTooltipVisible] = useState(false);

	return (
		<div
			className={css.versionBox}
			onMouseEnter={() => setTooltipVisible(true)}
			onMouseLeave={() => setTooltipVisible(false)}
		>
			<div className={css.versionType}>{props.type}</div>
			<div className={css.versionName}>{props.version}</div>
			{props.tooltip && tooltipVisible && (
				<div className={css.tooltipWrapper}>
					<div
						className={css.tooltip}
						style={{ right: props.mirrored ? -18 : undefined }}
					>
						{props.tooltip}
					</div>
				</div>
			)}
		</div>
	);
}

export function Header() {
	const dispatch = useAppDispatch();
	const { t, i18n } = useTranslation();
	const { instance } = useMsal();
	const logicsStatus = useAppSelector(selectFetchLogicsStatus);
	const backendStatus = useAppSelector(selectFetchBackendStatus);
	const editor = useAppSelector(selectEditor);
	const projectName = useAppSelector(selectProjectName);
	const activeAccount = instance.getActiveAccount();
	const lastUpdate = editor.oneDrive?.lastUpdate
		? new Date(editor.oneDrive?.lastUpdate).toLocaleString()
		: null;
	const allowedUsers = [
		"Gustavo Trentini",
		"Arthur Webber Balzani",
		"Guilherme Endres Rech",
	];

	const menuBarItems: MenuBarItemProps[] = [
		{ label: t("Project"), menuType: "project" },
		// { label: "Edit", menuType: "edit" },
		{ label: t("View"), menuType: "view" },
		// { label: "Help", menuType: "help" },
	];

	const [frontendVersion, frontendBuild] = env.frontendVersion.split("-");

	const changeLanguage = (language: string) => {
		i18n.changeLanguage(language);
	};

	const handleLogoutRedirect = () => {
		instance.logoutRedirect().catch((error) => console.log(error));
	};

	return (
		<div className={css.header}>
			<div className={css.headerColumn}>
				<div className={css.headerLine}>
					<div className={css.brandArea}>
						<div className={css.brandIcon}></div>
					</div>
					<div className={css.headerColumn}>
						<div className={css.headerLine}>
							<ProjectName />
							{lastUpdate && (
								<span className={css.lastUpdate}>
									{t("lastUpdate")} {lastUpdate}
								</span>
							)}
						</div>
						<div className={css.menuBar}>
							<div className={css.headerLine}>
								{menuBarItems.map((item) => (
									<MenuBarItem key={item.label} {...item} />
								))}
							</div>
						</div>
						{activeAccount && (
							<div className={css.headerColumn}>
								<div className={css.headerLine}>
									{t("welcome")} {activeAccount?.name}
								</div>
							</div>
						)}
					</div>
					<div className={css.versionArea}>
						<div style={{ display: "flex", gap: 10 }}>
							<img
								id="language-portuguese"
								className={css.flag}
								src={brazilFlag}
								onClick={() => changeLanguage("pt_br")}
							/>
							<img
								id="language-english"
								className={css.flag}
								src={usaFlag}
								onClick={() => changeLanguage("en")}
							/>
							<img
								id="language-spanish"
								className={css.flag}
								src={spanishFlag}
								onClick={() => changeLanguage("es")}
							/>
						</div>
						<VersionBox
							type="Frontend"
							version={
								parseInt(env.frontendVersion.split("-")[1]) === 0
									? `${frontendVersion}`
									: env.frontendVersion.split("-")[2].slice(1)
							}
							tooltip={
								env.branch !== "main" ? (
									<div className={css.versionName}>
										<div>
											Commit: {env.frontendVersion.split("-")[2].slice(1)}
										</div>
										<div>Version tag: {frontendVersion}</div>
										<div>
											Version build: {env.frontendVersion.split("-")[1]}
										</div>
										<div>Branch: {env.branch}</div>
										{/* {env.branch !== "main" && `-${env.branch}`} */}
									</div>
								) : undefined
							}
						/>
						{backendStatus.code === "succeeded" && (
							<VersionBox
								type="Backend"
								version={
									parseInt(backendStatus.backendVersion.split("-")[1]) === 0
										? `${backendStatus.backendVersion.split("-")[0]}`
										: backendStatus.backendVersion.split("-")[2].slice(1)
								}
								tooltip={
									env.branch !== "main" ? (
										<div className={css.versionName}>
											<div>
												Commit:{" "}
												{backendStatus.backendVersion.split("-")[2].slice(1)}
											</div>
											<div>
												Version tag:{" "}
												{backendStatus.backendVersion.split("-")[0]}
											</div>
											<div>
												Version build:{" "}
												{backendStatus.backendVersion.split("-")[1]}
											</div>
											<div>Branch: {env.branch}</div>
											{/* {env.branch !== "main" && `-${env.branch}`} */}
										</div>
									) : undefined
								}
							/>
						)}
						{backendStatus.code !== "succeeded" && (
							<div className={css.versionBox}>
								<div className={css.versionType}>Backend</div>
								<div className={css.versionName}>
									<div className={css.versionNotLoaded}>Not loaded</div>
								</div>
							</div>
						)}
						{logicsStatus.code === "succeeded" &&
							backendStatus.code === "succeeded" && (
								<VersionBox
									type="logics"
									version={`${logicsStatus.logics.commit.name}`}
									// 	${
									// 	logicsStatus.logics.commit.hash ===
									// 	backendStatus.commits.find(
									// 		(commit) =>
									// 			commit.hash === logicsStatus.logics.commit.hash,
									// 	)!.hash
									// 		? ""
									// 		: "*"
									// }
									tooltip={
										env.branch !== "main" ? (
											<div className={css.versionName}>
												<div>
													Version branch: {logicsStatus.logics.commit.name}
												</div>
												<div>
													Commit: {logicsStatus.logics.commit.hash.slice(0, 7)}
												</div>
												<div>
													Dataset version:{" "}
													{logicsStatus.logics.commit.datasetVersion ? (
														`V${logicsStatus.logics.commit.datasetVersion}`
													) : (
														<span className={css.versionNotLoaded}>
															Not available
														</span>
													)}
												</div>
											</div>
										) : undefined
									}
									mirrored
								/>
							)}
						{logicsStatus.code !== "succeeded" && (
							<div className={css.versionBox}>
								<div className={css.versionType}>{t("Logics")}</div>
								<div className={css.versionName}>
									<div className={css.versionNotLoaded}>Not loaded</div>
								</div>
							</div>
						)}
						<button
							className={css.signOutButton}
							onClick={handleLogoutRedirect}
						>
							{t("signOut")}
							<FontAwesomeIcon icon={faSignOut} />
						</button>
					</div>
				</div>
				<div className={css.toolBar}>
					{logicsStatus.code === "succeeded" && (
						<>
							<div
								className={css.toolBarItem}
								onClick={async () => {
									try {
										const { warnings, ...projectGraph } = pgbBuild(
											editor,
											logicsStatus.logics,
										);

										if (warnings && warnings.length) {
											toast.warning(
												<Toast title="Output Warning:" errors={warnings} />,
											);
										}

										const graph = JSON.stringify(projectGraph);

										const commitHash = logicsStatus.logics.commit.hash;

										const packet = {
											method: "POST",
											headers: {
												"Content-Type": "application/json",
											},
											body: JSON.stringify({
												graph,
												commitHash,
											}),
										};

										const fetch_result = await fetch(
											`${env.apiUrl}/export?commitHash=${commitHash}`,
											packet,
										);

										if (fetch_result.ok) {
											function download(
												content: any,
												fileName: string,
												contentType: string,
											) {
												let a = document.createElement("a");
												let file = new Blob([content], {
													type: contentType,
												});
												a.href = URL.createObjectURL(file);
												a.download = fileName;
												a.click();
											}

											download(
												await fetch_result.blob(),
												`${projectName}.dst`,
												"blob",
											);
										}
									} catch (error) {
										if (error instanceof Error) {
											toast.error(error.message);
											throw error;
										} else {
											const compilationErrors =
												error as Array<CompilationErrors>;

											if (compilationErrors.length > 0) {
												toast.error(
													<Toast
														title="Compilation Error:"
														errors={compilationErrors}
													/>,
												);
											}
										}
									}
								}}
							>
								{t("Export")}
							</div>
							{/* <div className={css.toolBarItem}>Generate Metadata</div> */}
							<div className={css.separator} />
							<div
								className={css.toolBarItem}
								onClick={() => {
									const windowSize = {
										x: 800,
										y: 500,
									};

									const windowPosition = {
										x: (window.innerWidth - windowSize.x) / 2,
										y: (window.innerHeight - windowSize.y) / 2,
									};

									dispatch(
										createWindow({
											title: t("Memory Manager"),
											typeAndProps: {
												type: "memoryManager",
												props: {},
											},
											position: windowPosition,
											size: windowSize,
										}),
									);
								}}
							>
								{t("Memory Manager")}
							</div>
							<div
								className={css.toolBarItem}
								onClick={() => {
									const windowSize = {
										x: 600,
										y: 500,
									};

									const windowPosition = {
										x: (window.innerWidth - windowSize.x) / 2,
										y: (window.innerHeight - windowSize.y) / 2,
									};

									dispatch(
										createWindow({
											title: t("DeviceManager"),
											typeAndProps: {
												type: "deviceManager",
												props: {},
											},
											position: windowPosition,
											size: windowSize,
										}),
									);
								}}
							>
								{t("Device Manager")}
							</div>
							<div className={css.separator} />
							{allowedUsers.includes(activeAccount?.name ?? "") && (
								<div
									className={css.toolBarItem}
									onClick={() => {
										const windowSize = {
											x: 500,
											y: 600,
										};

										const windowPosition = {
											x: Math.max(16, window.innerWidth - windowSize.x - 24),
											y: Math.max(16, (window.innerHeight - windowSize.y) / 2),
										};

										dispatch(
											createWindow({
												title: t("Chat"),
												typeAndProps: {
													type: "chat",
													props: {},
												},
												position: windowPosition,
												size: windowSize,
											}),
										);
									}}
								>
									{t("Chat")}
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
